import { createFileRoute } from '@tanstack/react-router';
import { useActiveOrganizationId } from '@interxfi/api-client';
import { PageLayout } from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { RequiresAccess } from '@interxfi/design-system/access-context';
import { HorizontalStack } from '@interxfi/design-system/components/alignment/horizontal-stack';
import { Button } from '@interxfi/design-system/components/button';
import type { CustomTableHeaderProps } from '@interxfi/design-system/components/table';
import PlusIcon from '@interxfi/design-system/icons/react/icon-plus';
import { type PortalTabbedNavigationTab, TabbedNavigationGroup } from '../../../../../components/router/router-tabbed-navigation';
import { useCanActAsOrganizationAdmin } from '../../../../../features/userauth/organization/userauth-organization-permission-helpers';
import { UserauthOrganizationMemberInvitationCreateDrawer } from '../../../../../features/userauth/organization-member-invitation/userauth-organization-member-invitation-create-drawer';
import { UserauthOrganizationMemberInvitationsTable } from '../../../../../features/userauth/organization-member-invitation/userauth-organization-member-invitations-table';
import { PlatformUsersTabRail } from '../../../../../features/userauth/platform-users-tab-rail';
import { getPageHead } from '../../../../../util/routing/meta';
import { usePlatformWorkspaceRollupViewContext } from '../../../../../util/view-context';

export const Route = createFileRoute('/_app/_platform/_context-required/user/invitation')({
  staticData: {
    tab: {
      group: TabbedNavigationGroup.PlatformUsers,
      label: 'Invitations',
      order: 1,
    },
  } satisfies PortalTabbedNavigationTab<TabbedNavigationGroup.PlatformUsers>,
  loader: () =>
    ({
      breadcrumb: buildBreadcrumb({
        id: 'user-invitation-list',
        label: 'Users',
        to: '/user/invitation',
      }),
    }) satisfies AggregateBreadcrumb,
  head: () => getPageHead('Invitations'),
  component: UserOrganizationInvitationList,
});

function TableHeader({ filter }: CustomTableHeaderProps) {
  const activeOrganizationId = useActiveOrganizationId();
  const canActAsAdmin = useCanActAsOrganizationAdmin(activeOrganizationId);

  return (
    <PlatformUsersTabRail
      actions={
        <HorizontalStack blockAlign="center" gap={2} wrap="nowrap">
          {canActAsAdmin ? (
            <RequiresAccess write>
              <UserauthOrganizationMemberInvitationCreateDrawer
                organizationId={activeOrganizationId}
                trigger={<Button affixIcon={<PlusIcon aria-hidden />} aria-label="Send an organization invitation" size="small" variant="primary" />}
              />
            </RequiresAccess>
          ) : null}

          {filter}
        </HorizontalStack>
      }
    />
  );
}

function UserOrganizationInvitationList() {
  const viewCtx = usePlatformWorkspaceRollupViewContext();

  return (
    <PageLayout>
      <PageLayout.Section noBorder>
        <UserauthOrganizationMemberInvitationsTable HeaderActions={TableHeader} view={viewCtx} />
      </PageLayout.Section>
    </PageLayout>
  );
}
